import type { Idea } from '@/lib/types';
import { calculateDaysSinceWatered } from '@/lib/time-utils';

interface CoFounderSentenceProps {
  ideas: Idea[];
}

/**
 * Generates a behavior-based truth line about the user's focus idea or overall state
 */
export function CoFounderSentence({ ideas }: CoFounderSentenceProps) {
  const focusIdea = ideas.find(i => i.is_focus && i.status === 'active');
  const activeIdeas = ideas.filter(i => i.status === 'active');
  
  // No ideas at all
  if (activeIdeas.length === 0) {
    return null;
  }
  
  // Has a focus idea - generate sentence about it
  if (focusIdea) {
    const days = calculateDaysSinceWatered(focusIdea);
    const sentence = getFocusSentence(days, focusIdea.title);
    return (
      <div className="text-center py-4 mb-6">
        <p className="text-sm text-muted-foreground/70 font-light italic">
          {sentence}
        </p>
      </div>
    );
  }
  
  // No focus idea - suggest setting one
  if (activeIdeas.length > 0) {
    return (
      <div className="text-center py-4 mb-6">
        <p className="text-sm text-muted-foreground/50 font-light italic">
          No idea in focus. Choose one to commit to.
        </p>
      </div>
    );
  }
  
  return null;
}

function getFocusSentence(days: number, title: string | null): string {
  const ideaName = title ? `"${title}"` : 'Your focus idea';
  
  if (days === 0) {
    return `You showed up today.`;
  }
  
  if (days === 1) {
    return `You returned yesterday.`;
  }
  
  if (days === 2) {
    return `${ideaName} has been quiet for 2 days.`;
  }
  
  if (days <= 4) {
    return `${ideaName} has been quiet for ${days} days.`;
  }
  
  if (days <= 6) {
    return `Momentum is thinning.`;
  }
  
  // 7+ days
  return `${ideaName} is fading.`;
}
